import React, { useState } from "react"
import { enqueueSnackbar } from "notistack"
import { createForumPost } from "../../services/forumService"
import { ForumPost, ForumCategoria, ForumCategoriaLabels } from "../../types/forum"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { PenSquare, Send } from "lucide-react"

interface Props {
  onPostCreated: (post: ForumPost) => void
}

export function ForumNovaPublicacao({ onPostCreated }: Props) {
  const [aberto, setAberto] = useState(false)
  const [conteudo, setConteudo] = useState("")
  const [categoria, setCategoria] = useState<ForumCategoria>(ForumCategoria.GERAL)
  const [loading, setLoading] = useState(false)

  const handleClose = () => {
    if (loading) return
    setAberto(false)
    setConteudo("")
    setCategoria(ForumCategoria.GERAL)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!conteudo.trim()) {
      enqueueSnackbar("O conteúdo não pode estar vazio.", { variant: "warning" })
      return
    }
    setLoading(true)
    try {
      const post = await createForumPost({ conteudo: conteudo.trim(), categoria })
      enqueueSnackbar("Publicação criada com sucesso!", { variant: "success" })
      onPostCreated(post)
      handleClose()
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao criar publicação", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setAberto(true)}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-1 py-1"
      >
        <PenSquare className="w-4 h-4" />
        Nova Publicação
      </button>

      <Dialog open={aberto} onOpenChange={(open) => { if (!open) handleClose() }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenSquare className="w-4 h-4 text-primary" />
              Nova Publicação
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Categoria</label>
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value as ForumCategoria)}
                disabled={loading}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {Object.values(ForumCategoria).map((cat) => (
                  <option key={cat} value={cat}>
                    {ForumCategoriaLabels[cat]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Conteúdo</label>
              <textarea
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                placeholder="O que você quer compartilhar?"
                rows={5}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                disabled={loading}
                autoFocus
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading || !conteudo.trim()} className="gap-1.5">
                {loading
                  ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}
                Publicar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
